import type { Event } from '@bugsnag/js'
import { doitEtreNotifiee } from '~/utils/bugsnag-filter'

/**
 * Écarte de Bugsnag les erreurs du fonctionnement normal — 401, 403, 404.
 *
 * Le module `nuxt-bugsnag` ne propose pas de filtre, et son `nuxt.config.ts` ne
 * peut pas en porter un : le module étale `options.config` dans
 * `runtimeConfig.public.bugsnag`, que ses plugins relisent au démarrage, or le
 * `runtimeConfig` est sérialisé et une fonction n'y survit pas. Poser `onError`
 * dans la configuration du module serait donc sans effet.
 *
 * Le filtre passe par `addOnError()`, l'API du SDK prévue pour enregistrer un
 * callback après le démarrage. Un callback qui renvoie `false` empêche l'envoi
 * de l'événement — il n'est donc pas décompté du quota, là où une règle posée
 * dans l'interface de Bugsnag le consommerait quand même.
 *
 * Ce plugin s'exécute après celui du module : `nuxt-bugsnag` enregistre le sien
 * par `addPlugin()` lors du `setup()`, donc avant les plugins applicatifs de
 * `app/plugins/`. Il n'est volontairement pas suffixé `.client` : le plugin du
 * module ne l'est pas non plus.
 *
 * Quand Bugsnag n'a pas démarré faute de clé, `useBugsnag()` renvoie le client
 * factice du module, dont `addOnError` est une fonction vide : l'appel reste
 * sans effet, sans erreur. Mais l'auto-import lui-même peut manquer, et la
 * référence n'est alors liée à rien : voir la garde posée dans le plugin.
 *
 * Le détail des codes retenus et leur justification sont dans
 * `app/utils/bugsnag-filter.ts`. Le pendant Nitro est dans
 * `server/plugins/bugsnag-filtre.ts`.
 */
export default defineNuxtPlugin(() => {
  /*
   * `useBugsnag()` est l'auto-import fourni par le module — mais il n'existe
   * pas toujours.
   *
   * Quand il manque, la référence n'est pas liée à un client factice : elle
   * n'est liée à rien du tout, et le plugin échoue sur `useBugsnag is not
   * defined`. Un plugin Nuxt qui jette casse l'initialisation de
   * l'application, laissant une page blanche — et son pendant Nitro fait
   * répondre 500 à chaque requête. Constaté en conditions réelles sur
   * `app-admin-site`, au démarrage sans clé.
   *
   * La cause exacte de la disparition reste à confirmer — cache `.nuxt/`
   * périmé, ordre de chargement, sortie prématurée du `setup()` du module.
   * La garde ci-dessous ne tranche pas cette question : elle protège quelle
   * que soit la raison, et ne coûte rien quand l'auto-import est bien là.
   *
   * Les deux tests qui suivent sont complémentaires, garder les deux :
   * `typeof useBugsnag` distingue la **fonction absente**, `addOnError` le
   * **client factice** que le module installe sans clé.
   */
  const bugsnag = typeof useBugsnag === 'function' ? useBugsnag() : null

  if (typeof bugsnag?.addOnError !== 'function') return

  bugsnag.addOnError((event: Event) => {
    // `false` annule l'envoi ; toute autre valeur le laisse passer.
    return doitEtreNotifiee(event.originalError)
  })
})
